/**
 * The product grid: search, supplier chips, and the cards.
 *
 * The card is the design's, and its two badges are load-bearing rather than
 * decorative:
 *
 * *   **SPECIAL**, with the "SAVE R30" flash — the was-price against the price.
 * *   **BULK DEAL** — the product has a price that improves with volume, which
 *     is the thing a merchant can reach by scheduling with other shops.
 *
 * Both are computed on the server (`saving`, `has_bulk_price`) rather than here,
 * so the same rule decides the badge on this grid, on the tablet, and on
 * anything else that renders a listing. A badge that appeared in one place and
 * not another would be read as a pricing error.
 */

import { useQuery } from "@tanstack/react-query";
import { Package, ShoppingCart, Users } from "lucide-react";
import { useState, type ReactNode } from "react";

import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Pill,
  SearchInput,
  SkeletonRows,
} from "@/components/ui";
import { cn } from "@/lib/cn";
import { formatMoney } from "@/lib/format";
import {
  marketplaceRepository,
  type Listing,
} from "@/repositories/marketplaceRepository";

import { MarketplaceShowcase, RetailerMark } from "./MarketplaceShowcase";
import { ScheduleDialog } from "./ScheduleDialog";
import { useAddToBasket } from "./useAddToBasket";

export function MarketplaceGrid() {
  const [search, setSearch] = useState("");
  const [supplier, setSupplier] = useState<string | null>(null);
  const [scheduling, setScheduling] = useState<Listing | null>(null);

  const suppliers = useQuery({
    queryKey: ["marketplace", "suppliers"],
    queryFn: () => marketplaceRepository.suppliers(),
  });

  const products = useQuery({
    queryKey: ["marketplace", "products", search, supplier],
    queryFn: () =>
      marketplaceRepository.products({
        q: search || undefined,
        supplier: supplier ?? undefined,
      }),
  });

  const landing = supplier === null && search === "";

  return (
    <div className="space-y-5">
      {/* The partner panels, until the merchant narrows the grid. "Shop Makro"
          is the same thing as tapping the Makro chip. */}
      {landing && suppliers.data && (
        <MarketplaceShowcase
          suppliers={suppliers.data}
          onShop={(id) => {
            setSupplier(id);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      )}

      <SearchInput
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search wholesale products"
        aria-label="Search wholesale products"
      />

      {/* The supplier chips from the design. A merchant chooses between two
          prices for the same maize meal by recognising the seller. */}
      <div className="flex flex-wrap gap-2">
        <Chip active={supplier === null} onClick={() => setSupplier(null)}>
          All suppliers
        </Chip>
        {suppliers.data?.map((entry) => (
          <Chip
            key={entry.id}
            active={supplier === entry.id}
            onClick={() => setSupplier(entry.id)}
          >
            <RetailerMark supplier={entry} />
          </Chip>
        ))}
      </div>


      {products.isError ? (
        <ErrorState
          title="Products could not load"
          onRetry={() => products.refetch()}
        />
      ) : products.isLoading ? (
        <SkeletonRows rows={6} />
      ) : products.data && products.data.results.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
          {products.data.results.map((listing) => (
            <ProductCard
              key={listing.id}
              listing={listing}
              onSchedule={() => setScheduling(listing)}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          title="Nothing matches"
          description="Try a different search, or clear the supplier filter."
        />
      )}

      <ScheduleDialog
        listing={scheduling}
        onClose={() => setScheduling(null)}
      />
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "flex h-9 items-center rounded-full border px-3 text-sm font-medium transition-colors",
        active
          ? "border-brand-500 bg-brand-50 text-brand-600"
          : "border-line bg-surface text-ink-600 hover:bg-canvas",
      )}
    >
      {children}
    </button>
  );
}

function ProductCard({
  listing,
  onSchedule,
}: {
  listing: Listing;
  onSchedule: () => void;
}) {
  const add = useAddToBasket(listing);

  return (
    <Card flush className="flex flex-col overflow-hidden">
      <div className="flex items-start justify-between gap-2 px-3 pt-3">
        <div className="flex gap-1">
          {listing.is_special && <Pill tone="danger">SPECIAL</Pill>}
          {listing.has_bulk_price && <Pill tone="info">BULK DEAL</Pill>}
        </div>
        <span className="text-xs font-medium text-ink-500">
          <RetailerMark supplier={listing.wholesaler} />
        </span>
      </div>

      <div className="flex h-32 items-center justify-center px-3 py-2">
        {listing.image ? (
          <img
            src={listing.image}
            alt=""
            className="max-h-full max-w-full object-contain"
            loading="lazy"
          />
        ) : (
          <Package className="size-10 text-ink-400" aria-hidden />
        )}
      </div>

      <div className="flex flex-1 flex-col gap-1 px-3">
        <p className="line-clamp-2 text-sm font-medium text-ink-900">
          {listing.name}
        </p>
        {listing.pack_size && (
          <p className="text-xs text-ink-500">{listing.pack_size}</p>
        )}

        <p className="mt-1 text-lg font-semibold tabular-nums text-danger-700">
          {formatMoney(listing.price)}
        </p>
        {listing.list_price && (
          <p className="text-xs text-ink-500">
            <span className="line-through">
              {formatMoney(listing.list_price)}
            </span>
            <span className="ml-2 font-medium text-money-700">
              Save {formatMoney(listing.saving)}
            </span>
          </p>
        )}
        {listing.price_per_unit && Number(listing.units_per_pack) > 1 && (
          <p className="text-xs text-ink-500">
            {formatMoney(listing.price_per_unit)} each
          </p>
        )}
        {listing.minimum_order_quantity > 1 && (
          <p className="text-xs text-ink-500">
            Minimum {listing.minimum_order_quantity}
          </p>
        )}
      </div>

      <div className="mt-3 space-y-2 p-3">
        <Button
          variant="money"
          fullWidth
          icon={<ShoppingCart className="size-4" />}
          loading={add.isPending}
          disabled={!listing.in_stock}
          onClick={() => add.mutate()}
        >
          {listing.in_stock ? "Add to order" : "Out of stock"}
        </Button>

        {/* The group-buying entry point, and the reason it is on the card rather
            than only on published deals: consolidation happens when enough shops
            want the same thing in the same week, which is not something a
            wholesaler has to have thought of first. */}
        {listing.has_bulk_price && (
          <Button
            variant="secondary"
            fullWidth
            size="sm"
            icon={<Users className="size-4" />}
            onClick={onSchedule}
          >
            Buy with other shops
          </Button>
        )}
      </div>
    </Card>
  );
}
